import { useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { useCurrentUser } from '~/hooks/useCurrentUser';

export type ViewingAsSession = components['schemas']['ViewingAsSession'];

const POLL_MS = 60_000;
// The server resolves an expired viewing token to the admin's own row, so the
// swap back only happens when /session is asked. Asking just after expires_at
// keeps the window in which a request runs as the admin short.
const EXPIRY_GRACE_MS = 1_000;

// A hard navigation, not SPA: the cookie changed identity, so the
// QueryClient must start clean.
const goToConsole = () => window.location.assign('/admin/people/');

export function useViewAsSession(): {
  viewingAs: ViewingAsSession | null;
  isViewing: boolean;
  // True until the first answer for a signed-in user. `isViewing` is false
  // for that whole window, so a guard that acts on "not viewing" must wait.
  sessionPending: boolean;
  end: () => Promise<void>;
} {
  const { me } = useCurrentUser();
  const { data, refetch, status } = api.getAuthSession.useQuery(undefined, {
    enabled: !!me,
    staleTime: POLL_MS,
    refetchOnWindowFocus: true,
    refetchInterval: (query) =>
      query.state.data?.viewing_as ? POLL_MS : false,
  });
  const { mutateAsync: endViewAs } = api.endViewAs.useMutation();

  const viewingAs = data?.viewing_as ?? null;
  const expiresAt = viewingAs?.expires_at;

  useEffect(() => {
    if (!expiresAt) return;
    const delay = Math.max(
      0,
      new Date(expiresAt).getTime() - Date.now() + EXPIRY_GRACE_MS,
    );
    // Every mounted instance arms this timer; `cancelRefetch: false` joins the
    // request already in flight instead of cancelling it.
    const timer = setTimeout(() => {
      void refetch({ cancelRefetch: false });
    }, delay);
    return () => clearTimeout(timer);
  }, [expiresAt, refetch]);

  // An expired viewing token resolves to the admin's row until /session is asked,
  // so a navigation asks at once rather than waiting for the poll.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lastPathname = useRef(pathname);
  const isViewing = viewingAs !== null;
  useEffect(() => {
    if (lastPathname.current === pathname) return;
    lastPathname.current = pathname;
    if (isViewing) void refetch({ cancelRefetch: false });
  }, [pathname, isViewing, refetch]);

  useEffect(() => {
    if (data?.view_as_ended === 'expired') goToConsole();
  }, [data?.view_as_ended]);

  const end = useCallback(async () => {
    await endViewAs({});
    goToConsole();
  }, [endViewAs]);

  return {
    viewingAs,
    isViewing: viewingAs !== null,
    sessionPending: !!me && status === 'pending',
    end,
  };
}
